import React, { useState, useMemo, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import FileUploadBox from 'src/features/DataUpload/components/organisms/FileUploadBox';
import { AnalyzingSpinner } from 'src/features/DataUpload/components/organisms/AnalyzingSpinner';
import DataVisualization from 'src/features/DataUpload/components/organisms/DataVisualization';
import { AnalysisState } from 'src/features/DataUpload/types';
import { captureException } from 'src/libs/sentry';
import { useUploadedData } from 'src/hooks/useUploadedData';
import { useAppContext } from 'src/contexts/AppContext';
import { UploadedFileInfo, UploadedDataResponse } from 'src/types';
import { customerDataUploadApiService } from 'src/api/customerDataUploadApiService';
import { formatFailedUploadsMessage } from 'src/features/DataUpload/utils/uploadErrorHelpers';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { CustomToast } from 'src/components/atoms/CustomToast';

type ApprovedFile = {
  fileId: string;
  fileName: string;
  approvedAt: Date;
  data: UploadedDataResponse['data'];
  originalFile?: File;
};

export const DataUploadView: React.FC = () => {
  const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
  const [approvedFiles, setApprovedFiles] = useState<ApprovedFile[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [previewData, setPreviewData] = useState<
    UploadedDataResponse['data'] | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName || '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  // Preload data during analysis phase to avoid delay when showing DataVisualization
  const {
    data: preloadedData,
    loading: _dataLoading,
    error: _dataError,
  } = useUploadedData({
    customerId,
    fileIds: uploadedFileIds,
    dataType: 'all',
    enabled:
      !!customerId &&
      uploadedFileIds.length > 0 &&
      analysisState === 'analyzing',
  });

  // Get all file IDs (current + approved) for combined analysis
  const _allFileIds = [
    ...uploadedFileIds,
    ...approvedFiles.map((f) => f.fileId),
  ];

  // Combine all approved files data
  const combinedApprovedFilesData = useMemo(() => {
    if (approvedFiles.length === 0) return null;

    const firstApprovedFile = approvedFiles[0];
    if (!firstApprovedFile?.data) return null;

    const combined = firstApprovedFile.data;

    return combined;
  }, [approvedFiles]);

  // Debug approved files data - check browser console for debug info

  const handleAnalyze = async (files: File[], fileIds: string[]) => {
    setAnalysisState('analyzing');

    // Check if we have fileIds available
    if (fileIds.length === 0) {
      setAnalysisState('idle');
      return;
    }

    // Store all fileIds and file names
    setUploadedFileIds(fileIds);
    setUploadedFileNames(files.map((file) => file.name));

    try {
      // Show AnalyzingSpinner while preloading data in background
      // Wait a bit for dramatic effect, then switch to completed state
      // Since data is being preloaded during analysis phase, there should be no delay
      setTimeout(() => {
        setAnalysisState('completed');
      }, 1000);
    } catch (error) {
      captureException({ error, fn: 'handleAnalyze' });
      setAnalysisState('idle'); // Reset to allow retry
    }
  };

  const handleResetToUpload = () => {
    setAnalysisState('idle');
    setUploadedFileIds([]);
    setUploadedFileNames([]);
    setIsPreviewMode(false);
    setPreviewFiles([]);
    setPreviewData(null);
  };

  const handlePreviewChart = useCallback(
    (files: File[], data: UploadedDataResponse['data']) => {
      setPreviewFiles(files);
      setPreviewData(data);
      setIsPreviewMode(true);
      setAnalysisState('completed');
    },
    [],
  );

  const handleUploadFromPreview = useCallback(async () => {
    if (!currentCustomer) return;

    // Prevent multiple simultaneous uploads
    if (isUploading) {
      return;
    }

    // Get all files to upload: preview files + approved files
    const filesToUpload: Array<{
      file: File;
      source: 'preview' | 'approved';
      fileName: string;
    }> = [];

    // Add preview files (if any)
    if (previewFiles.length > 0) {
      previewFiles.forEach((file) => {
        filesToUpload.push({ file, source: 'preview', fileName: file.name });
      });
    }

    // Add approved files that have original files
    approvedFiles.forEach((approvedFile) => {
      if (approvedFile.originalFile) {
        filesToUpload.push({
          file: approvedFile.originalFile,
          source: 'approved',
          fileName: approvedFile.fileName,
        });
      }
    });

    // Remove duplicates based on file name and size to prevent multiple uploads of the same file
    const uniqueFiles = filesToUpload.reduce(
      (acc, current) => {
        const existing = acc.find(
          (file) =>
            file.fileName === current.fileName &&
            file.file.size === current.file.size,
        );
        if (!existing) {
          acc.push(current);
        }
        return acc;
      },
      [] as typeof filesToUpload,
    );

    if (uniqueFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setAnalysisState('analyzing');

    try {
      const uploadResults = await Promise.all(
        uniqueFiles.map(async ({ file, source, fileName: _fileName }) => {
          try {
            // Get presigned URL
            const presignedResponse =
              await customerDataUploadApiService.getPresignedUrl({
                customerId: currentCustomer.customerName,
                fileName: file.name,
              });

            // Upload to S3
            await customerDataUploadApiService.uploadFileToS3(
              presignedResponse.presignedUrl,
              file,
            );

            // Extract file key from presigned URL
            const url = new URL(presignedResponse.presignedUrl);
            const fileKey = decodeURIComponent(url.pathname.substring(1));

            // Poll for status updates
            const finalProgress =
              await customerDataUploadApiService.pollUploadStatus(
                currentCustomer.customerName,
                fileKey,
                (_progress) => {
                  // Optional: show progress updates
                },
              );

            const finalStatus =
              finalProgress.status === 'succeeded' ? 'completed' : 'failed';
            return {
              status: finalStatus,
              fileId:
                finalStatus === 'completed' ? finalProgress.fileId : undefined,
              fileName: file.name,
              errorMessage: finalProgress.errorMessage,
              source,
            };
          } catch {
            return {
              status: 'failed' as const,
              fileName: file.name,
              errorMessage: undefined,
              source,
            };
          }
        }),
      );

      // Check if any files failed
      const failedFiles = uploadResults.filter(
        (result) => result.status === 'failed',
      );
      const successfulFiles = uploadResults.filter(
        (result) => result.status === 'completed',
      );

      if (failedFiles.length > 0) {
        showError(
          'Upload Error',
          formatFailedUploadsMessage(failedFiles),
          10000,
        );

        if (successfulFiles.length === 0) {
          setAnalysisState('idle');
          setIsUploading(false);
          return;
        }
      }

      // Store file IDs and names from successful uploads
      const fileIds = successfulFiles
        .map((result) => result.fileId)
        .filter((id): id is string => id !== undefined);

      const fileNames = successfulFiles.map((result) => result.fileName);

      setUploadedFileIds(fileIds);
      setUploadedFileNames(fileNames);
      setIsPreviewMode(false);
      setPreviewFiles([]);
      setPreviewData(null);

      // Clear approved files data after successful upload since data is now available through backend
      setApprovedFiles([]);

      showSuccess(
        'Upload Complete',
        `Successfully uploaded ${successfulFiles.length} new file(s) and processed ${approvedFiles.length} approved file(s). Total: ${successfulFiles.length} files.`,
        5000,
      );

      // Wait a bit for the data to be fully processed
      setTimeout(() => {
        setAnalysisState('completed');
        setIsUploading(false);
      }, 1000);
    } catch (error) {
      captureException({ error, fn: 'handleUploadFromPreview' });
      showError(
        'Upload Failed',
        'An error occurred during upload. Please try again.',
        5000,
      );
      setAnalysisState('idle');
      setIsUploading(false);
    }
  }, [
    currentCustomer,
    isUploading,
    previewFiles,
    approvedFiles,
    showSuccess,
    showError,
  ]);

  const handleApproveFiles = (fileInfo: UploadedFileInfo) => {
    // Store the current data when approving the file
    const fileData = preloadedData || previewData;

    if (!fileData) {
      return;
    }

    // Check if this is a combined file from multiple files (preview mode)
    if (fileData.fileInfo?.fileName?.includes(', ')) {
      // Split the combined filename back into individual files
      const fileNames = fileData.fileInfo.fileName.split(', ');
      const approvedFilesToAdd: ApprovedFile[] = [];

      fileNames.forEach((fileName, index) => {
        // Check if this individual file is already approved to prevent duplicates
        const isAlreadyApproved = approvedFiles.some(
          (file) => file.fileName === fileName.trim(),
        );

        if (isAlreadyApproved) {
          return;
        }

        const originalFile = previewFiles.find(
          (f) => f.name === fileName.trim(),
        );
        const approvedFile: ApprovedFile = {
          fileId: `${fileInfo.fileId}-${index}`, // Create unique ID for each file
          fileName: fileName.trim(),
          approvedAt: new Date(),
          data: fileData, // Store the combined data (since we can't separate it easily)
          originalFile: originalFile,
        };

        approvedFilesToAdd.push(approvedFile);
      });

      if (approvedFilesToAdd.length > 0) {
        setApprovedFiles((prev) => {
          const newApprovedFiles = [...prev, ...approvedFilesToAdd];
          return newApprovedFiles;
        });
      }
    } else {
      // Single file approval (original logic)
      // Check if file is already approved to prevent duplicates
      const isAlreadyApproved = approvedFiles.some(
        (file) => file.fileId === fileInfo.fileId,
      );

      if (isAlreadyApproved) {
        return; // Don't add duplicate approvals
      }

      // Find the corresponding file name from uploaded files
      const fileIndex = uploadedFileIds.findIndex(
        (id) => id === fileInfo.fileId,
      );
      const fileName =
        fileIndex >= 0 && uploadedFileNames[fileIndex]
          ? uploadedFileNames[fileIndex]
          : fileInfo.fileName;

      const originalFile = previewFiles.find((f) => f.name === fileName);

      const approvedFile: ApprovedFile = {
        fileId: fileInfo.fileId,
        fileName: fileName,
        approvedAt: new Date(),
        data: fileData,
        originalFile: originalFile,
      };

      setApprovedFiles((prev) => {
        const newApprovedFiles = [...prev, approvedFile];
        return newApprovedFiles;
      });
    }
    // Stay on the current analysis view instead of going back to upload
  };

  const renderContent = () => {
    switch (analysisState) {
      case 'analyzing':
        return (
          <AnalyzingSpinner
            message={
              isUploading ? 'Uploading and analyzing...' : 'Analyzing...'
            }
          />
        );

      case 'completed':
        if (isPreviewMode && previewData) {
          // Show preview mode with local data
          return (
            <DataVisualization
              preloadedData={previewData}
              showInternalLoading={false}
              onResetToUpload={handleResetToUpload}
              onUpload={handleUploadFromPreview}
              onApproveFiles={handleApproveFiles}
              approvedFiles={approvedFiles}
              approvedFilesData={combinedApprovedFilesData}
              isPreviewMode={true}
              isUploading={isUploading}
            />
          );
        } else if (uploadedFileIds.length > 0) {
          // Show uploaded data mode
          return (
            <DataVisualization
              fileIds={uploadedFileIds}
              showInternalLoading={false}
              preloadedData={preloadedData}
              onResetToUpload={handleResetToUpload}
              onApproveFiles={handleApproveFiles}
              approvedFiles={approvedFiles}
              approvedFilesData={combinedApprovedFilesData}
              uploadedFileNames={uploadedFileNames}
              isPreviewMode={false}
            />
          );
        } else {
          return <Box>No file IDs available</Box>;
        }

      default:
        return (
          <FileUploadBox
            onAnalyze={handleAnalyze}
            onPreviewChart={handlePreviewChart}
          />
        );
    }
  };

  return (
    <Box>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ bottom: '90px', right: '100px' }}
      />
      {renderContent()}
    </Box>
  );
};
